import React, { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate, Link } from "react-router-dom";
import { getPlotById } from "../services/api";
import { formatPrice } from "../utils/format";
import { Helmet } from "react-helmet";
import InspectionRequestModal from "../components/InspectionRequestModal";
import "../styles/PlotDetails.css";
import { formatUrl } from "../utils/config";
import { stripHtml } from "../utils/text";

const PlotDetails = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [plot, setPlot] = useState(location.state?.plot);
  const [loading, setLoading] = useState(!location.state?.plot);
  const [activeTab, setActiveTab] = useState("overview");
  const [imageError, setImageError] = useState(false);
  const [showInspectionModal, setShowInspectionModal] = useState(false);

  useEffect(() => {
    const loadPlot = async () => {
      if (!plot) {
        try {
          setLoading(true);
          const data = await getPlotById(id);
          setPlot(data);
        } catch (error) {
          console.error("Error loading plot:", error);
          navigate("/plots", { replace: true });
        } finally {
          setLoading(false);
        }
      }
    };

    loadPlot();
  }, [id, plot, navigate]);

  const createMarkup = (html) => {
    return { __html: html };
  };

  if (loading) {
    return (
      <div className="space-top space-extra-bottom">
        <div className="container">
          <div className="row">
            <div className="col-12 text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!plot) {
    return (
      <div className="space-top space-extra-bottom">
        <div className="container">
          <div className="row">
            <div className="col-12 text-center py-5">
              <h3>Plot not found</h3>
              <p>
                The plot you are looking for does not exist or has been removed.
              </p>
              <button className="th-btn" onClick={() => navigate("/plots")}>
                Back to Plots
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{`Plot ${plot.plotNumber} - ${
          plot.layout?.name || plot.estate?.name || "Plot Details"
        }`}</title>
        <meta
          name="description"
          content={`Details for Plot ${plot.plotNumber} located in ${
            plot.district ? `${plot.district}, ` : ""
          }${plot.lga}, ${plot.state}. ${
            plot.sizeSqm
          } sqm available for ${formatPrice(plot.price)}`}
        />
      </Helmet>

      <div
        className="breadcumb-wrapper"
        style={{
          backgroundImage: "url('/assets/img/main/m1.jpeg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="container">
          <div className="breadcumb-content">
            <h1 className="breadcumb-title">
              {`Plot ${plot.plotNumber}  ${plot.layout?.name || ""} ${
                plot.estate?.name ? `- ${plot.estate.name}` : ""
              }`}
            </h1>
            <ul className="breadcumb-menu">
              <li>
                <Link to="/plots">Plots</Link>
              </li>
              <li>{`Plot ${plot.plotNumber}`}</li>
            </ul>
          </div>
        </div>
      </div>

      <section className="space-top space-extra-bottom">
        <div className="container">
          <div className="row g-4">
            <div className="col-lg-8">
              <div className="property-main-image mb-4">
                <img
                  src={plot.photo || "/assets/img/defult.jpg"}
                  alt={`Plot ${plot.plotNumber}`}
                  className="img-fluid rounded w-100"
                  style={{ height: "500px", objectFit: "cover" }}
                  onError={() => setImageError(true)}
                />
              </div>

              <div className="property-details mb-5">
                <h2 className="h3 mb-3">Plot {plot.plotNumber}</h2>
                <p className="text-muted mb-4">
                  <i className="fas fa-map-marker-alt text-primary me-2"></i>
                  {plot.district ? `${plot.district}, ` : ""}
                  {plot.lga}, {plot.state}
                </p>

                <div className="property-features mb-4">
                  <div className="row g-3">
                    <div className="col-md-3 col-6">
                      <div className="feature-box text-center p-3 border rounded">
                        <i className="fas fa-ruler-combined fa-2x text-primary mb-2"></i>
                        <h6 className="mb-0">{plot.sizeSqm} sqm</h6>
                      </div>
                    </div>

                    <div className="col-md-3 col-6">
                      <div className="feature-box text-center p-3 border rounded">
                        <i className="fas fa-file-signature fa-2x text-primary mb-2"></i>
                        <h6 className="mb-0">
                          {formatPrice(plot.documentationFee || 0)}
                        </h6>
                        <small className="text-muted">Documentation</small>
                      </div>
                    </div>
                    <div className="col-md-3 col-6">
                      <div className="feature-box text-center p-3 border rounded">
                        <i className="fas fa-hard-hat fa-2x text-primary mb-2"></i>
                        <h6 className="mb-0">
                          {formatPrice(plot.developmentFee || 0)}
                        </h6>
                        <small className="text-muted">Development</small>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="property-description mb-5">
                  <div className="row">
                    <div className="col-md-8">
                      {plot.acquisitionType && (
                        <div className="mt-4">
                          <h5 className="h6 mb-2">Acquisition Type:</h5>
                          <p className="mb-0">{plot.acquisitionType}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {plot.features && (
                  <div className="property-features-list mb-5">
                    <h4 className="h5 mb-3">Features</h4>
                    <div
                      className="row g-1"
                      dangerouslySetInnerHTML={createMarkup(plot.features)}
                    />
                  </div>
                )}
              </div>

              {plot.locationType === "ESTATE" && plot.estate ? (
                <div className="location-info bg-light p-4 rounded mb-4 mt-4">
                  <h4 className="h5 mb-3">Estate Information</h4>
                  {plot.estate.thumbnail && (
                    <div className="location-thumbnail mb-3">
                      <img
                        src={plot.estate.thumbnail}
                        alt={plot.estate.name}
                        className="img-fluid rounded"
                        style={{
                          height: "150px",
                          width: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </div>
                  )}
                  <h5 className="h6">{plot.estate.name}</h5>
                  <p className="small text-muted mb-3">
                    {stripHtml(plot.estate.description)}
                  </p>

                  {plot.estate.features && (
                    <div className="location-features">
                      <h6 className="small fw-bold mb-2">Estate Features:</h6>
                      <div
                        className="row g-1 small"
                        dangerouslySetInnerHTML={createMarkup(
                          plot.estate.features
                        )}
                      />
                    </div>
                  )}
                </div>
              ) : (
                plot.locationType === "LAYOUT" &&
                plot.layout && (
                  <div className="location-info bg-light p-4 rounded mb-4 mt-4">
                    <h4 className="h5 mb-3">Layout Information</h4>
                    {plot.layout.thumbnail && (
                      <div className="location-thumbnail mb-3">
                        <img
                          src={formatUrl(plot.layout.thumbnail)}
                          alt={plot.layout.name}
                          className="img-fluid rounded"
                          style={{
                            height: "150px",
                            width: "100%",
                            objectFit: "cover",
                          }}
                        />
                      </div>
                    )}

                    <h5 className="h6">{plot.layout.name}</h5>
                    <p className="small text-muted mb-3">
                      {stripHtml(
                        plot.layout.description || "No description available."
                      )}
                    </p>

                    {plot.layout.features && (
                      <div className="location-features">
                        <h6 className="small fw-bold mb-2">Layout Features:</h6>
                        <div
                          className="row g-1 small"
                          dangerouslySetInnerHTML={createMarkup(
                            plot.layout.features
                          )}
                        />
                      </div>
                    )}
                  </div>
                )
              )}
            </div>

            <div className="col-lg-4">
              <div className="property-sidebar">
                <div className="property-price-card bg-light p-4 rounded mb-4">
                  <h3 className="h4 mb-3">
                    {formatPrice(plot.price)} -
                    <span>
                      {formatPrice(Number(plot.price) + Number(plot.price / 3))}
                    </span>
                  </h3>
                  <p className="text-muted mb-4">
                    <i className="fas fa-info-circle me-2"></i>
                    {plot.status === "AVAILABLE"
                      ? "Available"
                      : "Not Available"}
                  </p>

                  <div className="property-actions d-flex flex-column gap-3 mb-3">
                    <button
                      className="btn-reservation w-100"
                      disabled={plot.status !== "AVAILABLE"}
                      onClick={() => setShowInspectionModal(true)}
                    >
                      <i className="fas fa-search me-2"></i>
                      {plot.status === "AVAILABLE"
                        ? "Request Inspection"
                        : "Not Available"}
                    </button>
                    <Link
                      to={`/purchase/${plot.id}?type=plot`}
                      className="btn btn-contact w-100 text-decoration-none"
                    >
                      <i className="fas fa-file me-2"></i> Purchase Form
                    </Link>

                    <Link
                      to="/contact"
                      className="btn btn-primary w-100 text-decoration-none"
                    >
                      <i className="fas fa-envelope me-2"></i> Contact Us
                    </Link>

                    <Link
                      to="tel:+2347055558942"
                      className="btn btn-warning w-100 text-decoration-none"
                    >
                      <i className="fas fa-phone me-2"></i> Call Us
                    </Link>
                  </div>

                  <div className="property-meta mt-4 pt-3 border-top">
                    <ul className="list-unstyled mb-0">
                      <li className="d-flex justify-content-between py-2">
                        <span>Plot Number:</span>
                        <strong>{plot.plotNumber}</strong>
                      </li>
                      {plot.blockNumber && (
                        <li className="d-flex justify-content-between py-2">
                          <span>Block Number:</span>
                          <strong>{plot.blockNumber}</strong>
                        </li>
                      )}
                      {plot.layout?.name && (
                        <li className="d-flex justify-content-between py-2">
                          <span>Layout:</span>
                          <strong>{plot.layout.name}</strong>
                        </li>
                      )}
                      {plot.estate?.name && (
                        <li className="d-flex justify-content-between py-2">
                          <span>Estate:</span>
                          <strong>{plot.estate.name}</strong>
                        </li>
                      )}
                      <li className="d-flex justify-content-between py-2">
                        <span>Size:</span>
                        <strong>{plot.sizeSqm} sqm</strong>
                      </li>
                      <li className="d-flex justify-content-between py-2">
                        <span>Status:</span>
                        <strong
                          className={
                            plot.status === "AVAILABLE"
                              ? "text-success"
                              : "text-danger"
                          }
                        >
                          {plot.status || "Available"}
                        </strong>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <InspectionRequestModal
        isOpen={showInspectionModal}
        onClose={() => setShowInspectionModal(false)}
        propertyId={plot?.id}
      />
    </>
  );
};

export default PlotDetails;
